import { HUNT_BALANCE } from './balance';

/**
 * Allocated points -> effective combat attributes.
 *
 * Points are persisted as `bigint` and are **never** reduced: this module is
 * the one place where they become the `number` the physics and the renderer
 * work in, and it is deliberately the only place allowed to clamp.
 *
 * The clamp is renderer protection, NOT a level cap: the database keeps every
 * point a player ever spent, and raising the ceiling later changes nothing
 * about what was stored.
 */

/**
 * Largest point count the derived-stat arithmetic carries exactly.
 *
 * 2^40 ≈ 1.1e12 points. The largest per-point multiplier is health (10), so
 * the derived value stays below 1.1e13 - three orders of magnitude inside
 * `Number.MAX_SAFE_INTEGER`, with no rounding anywhere in between.
 *
 * A player who somehow passes it keeps every stored point; only the number the
 * simulation renders with stops growing.
 */
export const MAX_RENDERED_POINTS = 2n ** 40n;

/**
 * Safe `bigint` -> `number` for a point count.
 *
 * Never produces `Infinity` or `NaN`: an out-of-range value is clamped to the
 * documented ceiling instead of overflowing into a float.
 */
export function renderablePoints(points: bigint): number {
  if (points <= 0n) {
    return 0;
  }
  return Number(points > MAX_RENDERED_POINTS ? MAX_RENDERED_POINTS : points);
}

/**
 * Safe `bigint` -> `number` for an amount the HUD displays (a Gold balance, a
 * session total). Clamped at `Number.MAX_SAFE_INTEGER` so a display value can
 * never become an approximation pretending to be exact - the stored amount is
 * untouched and the wire always carries the full decimal string.
 */
export function renderableAmount(value: bigint): number {
  if (value <= 0n) {
    return 0;
  }
  const ceiling = BigInt(Number.MAX_SAFE_INTEGER);
  return Number(value > ceiling ? ceiling : value);
}

/** Base damage plus the damage points spent. */
export function effectiveBaseDamage(damagePoints: bigint): number {
  return (
    HUNT_BALANCE.ship.baseDamage +
    renderablePoints(damagePoints) * HUNT_BALANCE.allocation.damagePerPoint
  );
}

/** Base health plus the health points spent. */
export function effectiveBaseHealth(healthPoints: bigint): number {
  return (
    HUNT_BALANCE.ship.baseHealth +
    renderablePoints(healthPoints) * HUNT_BALANCE.allocation.healthPerPoint
  );
}

/**
 * Base attack speed plus the speed points spent.
 *
 * There is no cap and no smoothing: artifacts push this well past the ship's
 * own points (a level 10 Speed Fast is +30.72 on its own) and the value stays
 * honest, with the loop simply doing more work per tick. Whatever the cadence,
 * each shot stays a discrete projectile with its own damage - KENOCYBER never
 * aggregates a window of hits, and never converts shots into a beam.
 *
 * @see CLAUDE.md, "Projectiles are always discrete"
 */
export function effectiveBaseAttackSpeed(attackSpeedPoints: bigint): number {
  return (
    HUNT_BALANCE.ship.baseAttacksPerSecond +
    renderablePoints(attackSpeedPoints) * HUNT_BALANCE.allocation.attackSpeedPerPoint
  );
}
